import { SaleTerm } from 'constants/payments';
import { CheckoutUpgradeBilling } from 'constants/payloads/checkoutSession';

/**
 * Every number the two Dashboard post-checkout offers show, derived from one place.
 *
 * Landing paths this has to be right for:
 *  - Monthly or quarterly buyer, full price          → annual offer, exact receipt
 *  - Monthly or quarterly buyer with a checkout coupon → annual offer, exact receipt (same $396 year)
 *  - Legacy link without a Checkout Session amount    → annual offer, receipt at list price
 *  - Biweekly or unrecognised entry plan              → annual offer, generic receipt (no plan math)
 *  - Annual buyer, full price or discounted           → lifetime offer, exact all-in total
 *  - Unresolved Session (fail-closed to lifetime)     → lifetime offer, generic receipt
 *
 * Session-based offers use Stripe's invoice preview and recurring prices, including permanent
 * coupons. The quoted timestamp is carried through the existing upgrade call. List-price math is
 * retained only for legacy links without a Session. A failed Session lookup routes to lifetime.
 */
export const DASHBOARD_ANNUAL_OFFER_CENTS = 39_600;
export const DASHBOARD_REGULAR_ANNUAL_CENTS = 66_000;
export const DASHBOARD_REGULAR_MONTHLY_CENTS = 7_500;
export const DASHBOARD_REGULAR_QUARTERLY_CENTS = 19_500;
export const DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS = DASHBOARD_REGULAR_MONTHLY_CENTS * 12;
export const DASHBOARD_LIFETIME_OFFER_CENTS = 75_000;
export const DASHBOARD_LIFETIME_LATER_CENTS = 150_000;
export const DASHBOARD_HORIZON_YEARS = 5;

export interface DashboardEntryPlan {
  /** Yearly cost of staying on this plan. */
  annualCents: number;
  /** How the buyer will remember it: "a month", "every 3 months". */
  cadence: string;
  /** Adjective for comparisons: "monthly", "quarterly". */
  cadenceWord: string;
  listCents: number;
  name: string;
  perMonthCents: number;
  periodsPerYear: number;
  term: SaleTerm.Monthly | SaleTerm.Quarterly;
}

const ENTRY_PLANS: Partial<Record<SaleTerm, DashboardEntryPlan>> = {
  [SaleTerm.Monthly]: {
    annualCents: DASHBOARD_REGULAR_MONTHLY_CENTS * 12,
    cadence: 'a month',
    cadenceWord: 'monthly',
    listCents: DASHBOARD_REGULAR_MONTHLY_CENTS,
    name: 'Dashboard Monthly',
    perMonthCents: DASHBOARD_REGULAR_MONTHLY_CENTS,
    periodsPerYear: 12,
    term: SaleTerm.Monthly,
  },
  [SaleTerm.Quarterly]: {
    annualCents: DASHBOARD_REGULAR_QUARTERLY_CENTS * 4,
    cadence: 'every 3 months',
    cadenceWord: 'quarterly',
    listCents: DASHBOARD_REGULAR_QUARTERLY_CENTS,
    name: 'Dashboard Quarterly',
    perMonthCents: DASHBOARD_REGULAR_QUARTERLY_CENTS / 3,
    periodsPerYear: 4,
    term: SaleTerm.Quarterly,
  },
};

export type DashboardPaidSource = 'checkout-session' | 'list-price' | 'unavailable';

export interface DashboardFunnelPricingInput {
  billing?: CheckoutUpgradeBilling;
  checkoutAmountPaidCents?: number | null;
  currency?: string | null;
  startingTerm?: string | null;
}

interface DashboardResolvedPayment {
  currency: string;
  /** Exact amount from the completed Checkout Session, the plan's list price, or null. */
  paidCents: number | null;
  paidSource: DashboardPaidSource;
}

export interface DashboardAnnualOfferPricing extends DashboardResolvedPayment {
  creditCents: number | null;
  isRegularPriceComparison: boolean;
  /** Amount the buyer saved at checkout with a coupon or promotion code, when we can tell. */
  checkoutDiscountCents: number;
  /** Offer minus the amount paid at checkout; null when we do not know what was paid. */
  dueTodayCents: number | null;
  entry: DashboardEntryPlan | null;
  /** What the buyer pays for the first year: paid + due today. Falls back to the offer price. */
  firstYearTotalCents: number;
  horizonEntryCents: number | null;
  horizonOfferCents: number;
  horizonSavingsVsEntryCents: number | null;
  horizonYears: number;
  monthlyPlanAnnualCents: number;
  monthsFreePerYear: number | null;
  offerCents: number;
  offerPerMonthCents: number;
  percentOffEntry: number | null;
  percentOffMonthlyPlan: number;
  percentOffRegularAnnual: number;
  regularAnnualCents: number;
  savingsVsEntryCents: number | null;
  savingsVsMonthlyPlanCents: number;
  savingsVsRegularAnnualCents: number;
}

export interface DashboardLifetimeOfferPricing extends DashboardResolvedPayment {
  checkoutDiscountCents: number;
  /** True when we know the buyer is on Dashboard Annual (exact or list price). */
  entryKnown: boolean;
  horizonAnnualCents: number;
  horizonLifetimeCents: number;
  horizonMonthlyCents: number;
  horizonYears: number;
  laterLifetimeCents: number;
  laterTotalCents: number | null;
  lifetimeCents: number;
  monthlyPlanAnnualCents: number;
  percentOff: number;
  renewalAvoidedCents: number;
  renewalsToBreakEven: number;
  savingsOnThisPageCents: number;
  totalCents: number | null;
}

const isValidAmount = (amountCents: number | null | undefined): amountCents is number =>
  typeof amountCents === 'number' && Number.isFinite(amountCents) && amountCents >= 0;

const normalizeTerm = (term?: string | null) => (term || '').toUpperCase();

export const getDashboardEntryPlan = (term?: string | null): DashboardEntryPlan | null =>
  ENTRY_PLANS[normalizeTerm(term) as SaleTerm] || null;

export const normalizeDashboardCurrency = (currency?: string | null) => {
  const normalizedCurrency = currency?.trim().toLowerCase();

  if (!normalizedCurrency || !/^[a-z]{3}$/.test(normalizedCurrency)) {
    return 'usd';
  }

  try {
    new Intl.NumberFormat('en-US', {
      currency: normalizedCurrency.toUpperCase(),
      style: 'currency',
    }).format(0);
    return normalizedCurrency;
  } catch {
    return 'usd';
  }
};

export const formatDashboardCurrency = (
  amountCents: number | null | undefined,
  currency?: string | null,
) => {
  if (typeof amountCents !== 'number' || !Number.isFinite(amountCents)) {
    return 'Not available';
  }

  const fractionDigits = Number.isInteger(amountCents / 100) ? 0 : 2;

  return new Intl.NumberFormat('en-US', {
    currency: normalizeDashboardCurrency(currency).toUpperCase(),
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
  }).format(amountCents / 100);
};

export const percentOff = (fromCents: number, toCents: number) =>
  fromCents > 0 ? Math.round(((fromCents - toCents) / fromCents) * 100) : 0;

/**
 * "nearly 7 months", "over 5 months", "6 months" — for the months-free reframe. Rounds toward the
 * buyer's benefit only in wording, never in the number it describes.
 */
export const describeMonths = (months: number) => {
  const safeMonths = Math.max(0, months);
  const whole = Math.floor(safeMonths);
  const fraction = safeMonths - whole;
  const unit = (count: number) => `${count} month${count === 1 ? '' : 's'}`;

  if (fraction < 0.05) {
    return unit(whole);
  }
  if (fraction >= 0.5) {
    return `nearly ${unit(whole + 1)}`;
  }
  return `over ${unit(whole)}`;
};

const resolvePayment = (
  { checkoutAmountPaidCents, currency }: DashboardFunnelPricingInput,
  listCents: number | null,
): DashboardResolvedPayment => {
  const normalizedCurrency = normalizeDashboardCurrency(currency);

  if (isValidAmount(checkoutAmountPaidCents)) {
    return {
      currency: normalizedCurrency,
      paidCents: Math.round(checkoutAmountPaidCents),
      paidSource: 'checkout-session',
    };
  }

  if (listCents !== null) {
    return { currency: normalizedCurrency, paidCents: listCents, paidSource: 'list-price' };
  }

  return { currency: normalizedCurrency, paidCents: null, paidSource: 'unavailable' };
};

const discountFrom = (payment: DashboardResolvedPayment, listCents: number | null) =>
  payment.paidSource === 'checkout-session' && listCents !== null && payment.paidCents !== null
    ? Math.max(listCents - payment.paidCents, 0)
    : 0;

export const getDashboardAnnualOfferPricing = (
  input: DashboardFunnelPricingInput,
): DashboardAnnualOfferPricing => {
  const baseEntry = getDashboardEntryPlan(input.startingTerm);
  const currentRate = input.billing?.currentRecurringCents;
  const resolvedEntry =
    baseEntry && currentRate !== undefined
      ? {
          ...baseEntry,
          listCents: currentRate,
          perMonthCents: (currentRate * baseEntry.periodsPerYear) / 12,
          annualCents: currentRate * baseEntry.periodsPerYear,
        }
      : baseEntry;
  const payment = resolvePayment(input, baseEntry?.listCents ?? null);
  const quote = input.billing?.annualOffer;
  const offerCents = quote?.renewalAmountCents ?? DASHBOARD_ANNUAL_OFFER_CENTS;
  // Only anchor against the buyer's current plan when the annual offer actually beats it. A
  // permanent coupon restricted to the monthly/quarterly Stripe Product (not the annual one) can
  // leave a buyer paying less per year than this offer; anchoring there would render negative
  // "savings", negative "months free", and a negative percentage on a payment page. Degrade to the
  // regular-annual framing, which is always a real saving, exactly as an unrecognised plan does.
  const entry = resolvedEntry && offerCents < resolvedEntry.annualCents ? resolvedEntry : null;
  const dueTodayCents = quote
    ? quote.amountDueTodayCents
    : payment.paidCents !== null
    ? Math.max(offerCents - payment.paidCents, 0)
    : null;
  const firstYearTotalCents =
    payment.paidCents !== null && dueTodayCents !== null
      ? payment.paidCents + dueTodayCents
      : offerCents;
  const savingsVsEntryCents = entry ? entry.annualCents - offerCents : null;

  return {
    ...payment,
    checkoutDiscountCents: discountFrom(payment, baseEntry?.listCents ?? null),
    creditCents: quote?.creditCents ?? payment.paidCents,
    isRegularPriceComparison: input.billing?.currentRenewalIsTemporary ?? false,
    dueTodayCents,
    entry,
    firstYearTotalCents,
    horizonEntryCents: entry ? entry.annualCents * DASHBOARD_HORIZON_YEARS : null,
    horizonOfferCents: offerCents * DASHBOARD_HORIZON_YEARS,
    horizonSavingsVsEntryCents:
      savingsVsEntryCents !== null ? savingsVsEntryCents * DASHBOARD_HORIZON_YEARS : null,
    horizonYears: DASHBOARD_HORIZON_YEARS,
    monthlyPlanAnnualCents: DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS,
    monthsFreePerYear:
      entry && entry.perMonthCents > 0 ? 12 - offerCents / entry.perMonthCents : null,
    offerCents,
    offerPerMonthCents: offerCents / 12,
    percentOffEntry: entry ? percentOff(entry.annualCents, offerCents) : null,
    percentOffMonthlyPlan: percentOff(DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS, offerCents),
    percentOffRegularAnnual: percentOff(DASHBOARD_REGULAR_ANNUAL_CENTS, offerCents),
    regularAnnualCents: DASHBOARD_REGULAR_ANNUAL_CENTS,
    savingsVsEntryCents,
    savingsVsMonthlyPlanCents: DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS - offerCents,
    savingsVsRegularAnnualCents: DASHBOARD_REGULAR_ANNUAL_CENTS - offerCents,
  };
};

export const getDashboardLifetimeOfferPricing = (
  input: DashboardFunnelPricingInput,
): DashboardLifetimeOfferPricing => {
  const isAnnual = normalizeTerm(input.startingTerm) === SaleTerm.Annual;
  const renewalCents = input.billing?.currentRecurringCents ?? DASHBOARD_REGULAR_ANNUAL_CENTS;
  const entryKnown = isAnnual && !input.billing?.currentRenewalIsTemporary && renewalCents > 0;
  const payment = resolvePayment(input, isAnnual ? DASHBOARD_REGULAR_ANNUAL_CENTS : null);
  const totalCents =
    payment.paidCents !== null ? payment.paidCents + DASHBOARD_LIFETIME_OFFER_CENTS : null;

  return {
    ...payment,
    checkoutDiscountCents: discountFrom(payment, isAnnual ? DASHBOARD_REGULAR_ANNUAL_CENTS : null),
    entryKnown,
    horizonAnnualCents: entryKnown
      ? (payment.paidCents ?? renewalCents) + renewalCents * (DASHBOARD_HORIZON_YEARS - 1)
      : DASHBOARD_REGULAR_ANNUAL_CENTS * DASHBOARD_HORIZON_YEARS,
    horizonLifetimeCents:
      totalCents ?? DASHBOARD_REGULAR_ANNUAL_CENTS + DASHBOARD_LIFETIME_OFFER_CENTS,
    horizonMonthlyCents: DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS * DASHBOARD_HORIZON_YEARS,
    horizonYears: DASHBOARD_HORIZON_YEARS,
    laterLifetimeCents: DASHBOARD_LIFETIME_LATER_CENTS,
    laterTotalCents:
      payment.paidCents !== null ? payment.paidCents + DASHBOARD_LIFETIME_LATER_CENTS : null,
    lifetimeCents: DASHBOARD_LIFETIME_OFFER_CENTS,
    monthlyPlanAnnualCents: DASHBOARD_REGULAR_MONTHLY_ANNUAL_CENTS,
    percentOff: percentOff(DASHBOARD_LIFETIME_LATER_CENTS, DASHBOARD_LIFETIME_OFFER_CENTS),
    renewalAvoidedCents: entryKnown ? renewalCents : DASHBOARD_REGULAR_ANNUAL_CENTS,
    renewalsToBreakEven: Math.ceil(
      DASHBOARD_LIFETIME_OFFER_CENTS / (entryKnown ? renewalCents : DASHBOARD_REGULAR_ANNUAL_CENTS),
    ),
    savingsOnThisPageCents: DASHBOARD_LIFETIME_LATER_CENTS - DASHBOARD_LIFETIME_OFFER_CENTS,
    totalCents,
  };
};
